import { FC } from "preact/compat";
import { useEffect } from "preact/hooks";
import {
    Navigate,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { me, useApp } from "~/core/store";
import {
    DiscoveryEditor,
    DiscoveryHistory,
    IssueTracker,
    Meta,
    Snacker,
} from "~/organisms";
import Content from "./Content";
import Header from "./Header";
import LoadingScreen from "./LoadingScreen";
import MainSearch from "./MainSearch";
import Menu from "./Menu";
import SettingsActions from "./SettingsActions";
import Visits from "./Visits";

let initialized = false;

const Main: FC = () => {
    const {
        api: {
            connect,
            installation,
            company,
            connection,
            connecting,
            service,
            waiting,
            feeCode,
            project,
            task,
            time,
            feeCategory,
            client,
            group,
            settings,
            employee,
            currency,
            account,
            me: meApi,
        },
        share,
        settings: { setup: setupSettings },
    } = useApp();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const params = useParams();
    const searchStr = searchParams.toString();
    // @TODO: check for only allowed params in order to prevent XSS
    const appendParams = searchStr ? `&${searchStr}` : "";

    useEffect(() => {
        if (!connection.peek()) {
            connect(params.installation);
        }
    }, [connect, params.installation, connection]);

    useEffect(() => {
        if (connection.value) {
            setupSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection.value]);

    useEffect(() => {
        if (!connection.value || !service.loaded.value || initialized) {
            return;
        }

        if (service.enabledPeek("User", "Get")) {
            meApi.listen();
        }

        if (
            service.enabledPeek("ConnectedAccount", "List") &&
            service.enabledPeek("ExternalAccount", "List")
        ) {
            account.listen();
        }

        if (service.enabledPeek("Task", "Subscribe")) {
            task.subscribe();
        }

        if (service.enabledPeek("TaskUser", "Subscribe")) {
            task.subscribeTaskUser();
        }

        if (service.enabledPeek("TaskAssignment", "Subscribe")) {
            task.assignment.subscribe();
        }

        if (service.enabledPeek("TrackGroup", "Subscribe")) {
            time.group.listen();
        }

        if (service.enabledPeek("Track", "Subscribe")) {
            time.track.listenActive();
        }

        if (service.enabledPeek("UserSettings", "Get")) {
            settings.listen();
        }

        if (service.enabledPeek("ProSettings", "Get")) {
            settings.pro.listen();
        }

        if (service.enabledPeek("Expense", "Settings")) {
            settings.expense.listen();
        }

        if (service.enabledPeek("CompanySettings", "Get")) {
            settings.company.listen();
        }

        if (service.enabledPeek("Project", "List")) {
            project.listen();
        }

        if (service.enabledPeek("Client", "List")) {
            client.listen();
        }

        if (service.enabledPeek("Group", "List")) {
            group.listen();
        }

        if (service.enabledPeek("Feecode", "List")) {
            feeCode.listen();
        }

        if (service.enabledPeek("Feecategory", "List")) {
            feeCategory.listen();
        }

        if (service.enabledPeek("Employee", "List")) {
            employee.listen();
        }

        if (service.enabledPeek("Currency", "List")) {
            currency.listen();
        }

        share.check();

        initialized = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection.value, service.loaded.value]);

    if (connecting.value || waiting.value) {
        return <LoadingScreen />;
    }

    if (!connection.value && !waiting.value && !connecting.value) {
        const failed = params.installation === "_";

        return (
            <Navigate
                to={`/p/login?installation=${params.installation}&continue=${pathname}${appendParams}${failed ? "&error=fail&prompt=force" : ""}`}
            />
        );
    }

    if (
        params.installation !== installation.value ||
        params.company !== company.value
    ) {
        return <Navigate to={me("dashboard")} replace />;
    }

    return (
        <div
            data-main
            className="max-w-screen flex h-screen max-h-screen w-screen flex-col overflow-hidden text-[#454554]"
        >
            <Header />
            <Snacker />
            <Visits />
            <Meta />
            <SettingsActions />
            <MainSearch />
            <IssueTracker />
            <DiscoveryEditor />
            <div className="flex h-full">
                <Menu />
                <Content />
            </div>
            <DiscoveryHistory />
        </div>
    );
};

export default Main;
